import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Users, Image, Plus, Save } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  generation_count: number;
  free_generations_used: number;
  subscription_status: string;
  ethnicity: string | null;
  created_at: string;
}

interface Stats {
  totalUsers: number;
  totalGenerations: number;
}

interface Generation {
  id: string;
  hairstyle_name: string;
  original_photo_url: string;
  result_photo_url: string;
  created_at: string;
}

const Analytics = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [stats, setStats] = useState<Stats>({ totalUsers: 0, totalGenerations: 0 });
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [editForm, setEditForm] = useState({
    generation_count: 0,
    free_generations_used: 0,
  });
  const [viewingGenerations, setViewingGenerations] = useState<{ user: UserProfile; generations: Generation[] } | null>(null);
  const [loadingGenerations, setLoadingGenerations] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/");
      toast({
        title: "Access denied",
        description: "You don't have permission to access this page.",
        variant: "destructive",
      });
    }
  }, [isAdmin, authLoading, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchAnalytics();
    }
  }, [isAdmin]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch all users
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // Fetch total generations count
      const { count: generationsCount, error: genError } = await supabase
        .from('generations')
        .select('*', { count: 'exact', head: true });

      if (genError) throw genError;

      setUsers(usersData || []);
      setStats({
        totalUsers: usersData?.length || 0,
        totalGenerations: generationsCount || 0,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: UserProfile) => {
    setEditingUser(user);
    setEditForm({
      generation_count: user.generation_count,
      free_generations_used: user.free_generations_used,
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          generation_count: editForm.generation_count,
          free_generations_used: editForm.free_generations_used,
        })
        .eq('id', editingUser.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "User updated successfully",
      });

      setEditingUser(null);
      fetchAnalytics();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleAddGenerations = (user: UserProfile, amount: number) => {
    setEditingUser(user);
    setEditForm({
      generation_count: user.generation_count + amount,
      free_generations_used: user.free_generations_used,
    });
  };

  const handleViewGenerations = async (user: UserProfile) => {
    setLoadingGenerations(true);
    try {
      const { data, error } = await supabase
        .from('generations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setViewingGenerations({ user, generations: data || [] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingGenerations(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/admin")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin
          </Button>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Analytics Dashboard
          </h1>
          <div className="w-24" />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
              <p className="text-xs text-muted-foreground">Registered accounts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Generations</CardTitle>
              <Image className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalGenerations}</div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>User Management</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Paid Gens</TableHead>
                    <TableHead className="text-right">Free Gens</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>{user.full_name || "-"}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          user.subscription_status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {user.subscription_status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{user.generation_count}</TableCell>
                      <TableCell className="text-right">{user.free_generations_used}</TableCell>
                      <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewGenerations(user)}
                          disabled={loadingGenerations}
                        >
                          View Gens
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleAddGenerations(user, 10)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          10
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update generation counts for {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="generation_count">Paid Generations Count</Label>
              <Input
                id="generation_count"
                type="number"
                value={editForm.generation_count}
                onChange={(e) => setEditForm({ ...editForm, generation_count: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted-foreground">
                For subscription users
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="free_generations_used">Free Generations Used</Label>
              <Input
                id="free_generations_used"
                type="number"
                value={editForm.free_generations_used}
                onChange={(e) => setEditForm({ ...editForm, free_generations_used: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted-foreground">
                Out of 3 free generations
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveUser}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Generations Dialog */}
      <Dialog open={!!viewingGenerations} onOpenChange={(open) => !open && setViewingGenerations(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generations for {viewingGenerations?.user.email}</DialogTitle>
            <DialogDescription>
              Total: {viewingGenerations?.generations.length || 0} generations
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {viewingGenerations?.generations.map((gen) => (
              <Card key={gen.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">{gen.hairstyle_name}</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {new Date(gen.created_at).toLocaleString()}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <p className="text-xs font-medium mb-1">Original:</p>
                    <img 
                      src={gen.original_photo_url} 
                      alt="Original" 
                      className="w-full h-48 object-contain rounded bg-muted"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-medium mb-1">Result:</p>
                    <img 
                      src={gen.result_photo_url} 
                      alt="Result" 
                      className="w-full h-48 object-contain rounded bg-muted"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            {viewingGenerations?.generations.length === 0 && (
              <div className="col-span-2 text-center py-8 text-muted-foreground">
                No generations yet
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
